import {
  findFacilityById,
  findPartnerById,
  findProfessionalById,
  findProfessionalRatingById,
} from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import {
  findSchedulesByProcedures,
} from '@/app/(main)/(pages)/busca/[slug]/_services/find-schedules-by-procedures.service';
import { mapToSearchPartnerData } from '@/app/(main)/(pages)/_services/search-api/map-to-search-partner-data.util';
import {
  SearchApiFacility,
  SearchApiProcedure,
  SearchApiProcedureType,
  SearchApiScheduleGroup,
} from '@/app/(main)/(pages)/_types/search-api-response.type';
import { SearchPartnerData, SearchType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { getLogger } from '@/logger/logger';
import { toCitySlug } from '@/utils/formatters/city/to-city-slug.util';
import { minResultsToHideSuggestions } from '@/utils/consts/search-schedules.consts';

export const NEAR_CITY_SUGGESTION_LIMIT = 2;
export const NEAR_CITY_PAGE_SIZE = 5;
const NEAR_CITY_INITIAL_DISTANCE = 10;
const NEAR_CITY_EXPANDED_DISTANCE = 30;

export type NearCitySchedules = {
  schedules: SearchPartnerData[];
  pendingGroups: SearchApiScheduleGroup[];
  cityCount: number;
  distance: number;
  hasMore: boolean;
};

export type NearCitySchedulesPage = {
  schedules: SearchPartnerData[];
  hasMore: boolean;
};

export type GetNearCitySchedulesParams = {
  procedureCodes: string[];
  searchType: string;
  city?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
};

export type GetNearCitySchedulesPageParams = GetNearCitySchedulesParams & {
  distance: number;
  page: number;
};

export function hasRenderableNearCitySuggestions(
  nearCity: NearCitySchedules | null,
): nearCity is NearCitySchedules {
  return Boolean(nearCity?.schedules?.length);
}

function buildProcedure(group: SearchApiScheduleGroup): SearchApiProcedure {
  const procedure = group.procedures[0];

  return {
    id: procedure?.id ?? '',
    name: procedure?.name ?? '',
    code: '',
    tussCode: '',
    slug: procedure?.slug ?? '',
    type: SearchApiProcedureType.NON_LABORATORY,
    availableForTelehealth: false,
  };
}

type FacilityById = Map<string, Promise<SearchApiFacility | null>>;

function loadFacilitiesOnce(groups: SearchApiScheduleGroup[]): FacilityById {
  const facilityIds = [...new Set(groups.map((group) => group.partnerFacilityId).filter(Boolean))] as string[];

  return new Map(facilityIds.map((id) => [id, findFacilityById(id).catch(() => null)]));
}

async function hydrateCard(
  group: SearchApiScheduleGroup,
  searchType: string,
  facilityById: FacilityById,
): Promise<SearchPartnerData> {
  const procedure = buildProcedure(group);
  const facilityPromise = group.partnerFacilityId
    ? facilityById.get(group.partnerFacilityId) ?? Promise.resolve(null)
    : Promise.resolve(null);

  const [professional, partner, facility, rating] = await Promise.all([
    group.professionalId ? findProfessionalById(group.professionalId).catch(() => null) : Promise.resolve(null),
    group.partnerId ? findPartnerById(group.partnerId).catch(() => null) : Promise.resolve(null),
    facilityPromise,
    group.professionalId ? findProfessionalRatingById(group.professionalId).catch(() => null) : Promise.resolve(null),
  ]);

  return mapToSearchPartnerData({
    group,
    procedures: [procedure],
    professional,
    partner,
    facility,
    rating,
    searchType: searchType as SearchType,
  });
}

async function countDistinctCities(facilityById: FacilityById): Promise<number> {
  const facilities = await Promise.all(facilityById.values());
  const cities = new Set(facilities.map((facility) => facility?.city).filter(Boolean));

  return cities.size;
}

function buildLocationParams(params: GetNearCitySchedulesParams) {
  return {
    procedureCodes: params.procedureCodes,
    cityToExclude: toCitySlug(params.city, params.state),
    latitude: params.latitude,
    longitude: params.longitude,
  };
}

export async function getNearCitySchedules(
  params: GetNearCitySchedulesParams,
): Promise<NearCitySchedules | null> {
  if (params.searchType === SearchType.ONLINE_CONSULTATION) {
    return null;
  }

  const logger = getLogger('search.near-city-schedules');
  const locationParams = buildLocationParams(params);

  if (!locationParams.cityToExclude) {
    logger.warn('Skipped near-city suggestions without a city slug', {
      search: {
        event: 'near_city_skipped_missing_city_slug',
        has_city: Boolean(params.city),
        has_state: Boolean(params.state),
      },
    });

    return null;
  }

  try {
    const base = { ...locationParams, limit: minResultsToHideSuggestions };

    let distance = NEAR_CITY_INITIAL_DISTANCE;
    let { groups, hasMore } = await findSchedulesByProcedures({ ...base, distance });

    if (groups.length < minResultsToHideSuggestions) {
      distance = NEAR_CITY_EXPANDED_DISTANCE;
      ({ groups, hasMore } = await findSchedulesByProcedures({ ...base, distance }));
    }

    if (!groups.length) {
      return null;
    }

    const facilityById = loadFacilitiesOnce(groups);

    const [schedules, cityCount] = await Promise.all([
      Promise.all(
        groups
          .slice(0, NEAR_CITY_SUGGESTION_LIMIT)
          .map((group) => hydrateCard(group, params.searchType, facilityById)),
      ),
      countDistinctCities(facilityById),
    ]);

    return {
      schedules,
      pendingGroups: groups.slice(NEAR_CITY_SUGGESTION_LIMIT),
      cityCount,
      distance,
      hasMore,
    };
  } catch (error) {
    logger.error('Failed to fetch near-city suggestions', {
      search: { event: 'near_city_failed', procedure_codes: params.procedureCodes },
      error,
    });

    return null;
  }
}

export async function hydrateNearCityGroups(
  groups: SearchApiScheduleGroup[],
  searchType: string,
): Promise<SearchPartnerData[]> {
  const facilityById = loadFacilitiesOnce(groups);

  return Promise.all(groups.map((group) => hydrateCard(group, searchType, facilityById)));
}

export async function getNearCitySchedulesPage(
  params: GetNearCitySchedulesPageParams,
): Promise<NearCitySchedulesPage> {
  const locationParams = buildLocationParams(params);

  if (!locationParams.cityToExclude) {
    return { schedules: [], hasMore: false };
  }

  const { groups, hasMore } = await findSchedulesByProcedures({
    ...locationParams,
    distance: params.distance,
    page: params.page,
    limit: NEAR_CITY_PAGE_SIZE,
  });

  return { schedules: await hydrateNearCityGroups(groups, params.searchType), hasMore };
}
